import {CommonModule} from '@angular/common';
import {Component, HostListener, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AnnouncementService} from '../../shared/services/announcement.service';
import {Announcement} from '../../models/announcements-model';
import {ApplicationSettingsService} from '../../shared/services/application-settings.service';
import {ApplicationSettings, DisplayModes} from '../../models/application-settings.model';
import {Modal} from 'bootstrap';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {AppConstants} from '../../shared/app-constants';
import {AuthService} from '../../shared/services/auth.service';
import {ErrorService} from '../../shared/services/error.service';
import {AuthTokenResponse} from '../../models/auth-token-response.model';
import {LandingPageModuleService} from '../../shared/services/landing-page-module.service';
import {LandingPageModule} from '../../models/landing-page-module-model';
import {PaymentCategory} from '../../models/additional-pac-data.model';
import {LocalStorageUtils} from '../../shared/utils/local-storage-utils';
import {ErrorBannerComponent} from '../../components/error-banner/error-banner.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: true,
  imports: [CommonModule, ErrorBannerComponent
  ],
})
export class HomeComponent implements OnInit {
  accordionExpanded: boolean = false;
  announcements: Announcement[] = [];
  token: string = '';
  applicationSettings: ApplicationSettings[] = [];
  displayMode!: DisplayModes;
  selectedAnnouncement: SafeHtml = '';
  displayModes = DisplayModes;
  errorMessage: string | null = '';
  onScreenKeyboardEnabled: boolean = false;

  courtPaymentModules: LandingPageModule[] = [];
  courtDepositModules: LandingPageModule[] = [];
  chunkedCourtPaymentModules: any[][] = [];
  chunkedCourtDepositModules: any[][] = [];
  depositCategorySelected: string | null = null;
  isTerminal: boolean = false;
  //Below members are to track scanned text from QR scanner
  scanBuffer = '';
  scanTimeout?: ReturnType<typeof setTimeout>;
  scannedValue = '';


  chunkArray(arr: any[], size: number): any[][] {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  navigateToCourtPayment(paymentType: string | null) {
    this.router.navigate(['court-payment-landing'], {
      state: {
        paymentType
      }
    });
  }

  selectCourtDepositModule(module: LandingPageModule) {
    this.depositCategorySelected = module.paymentTypeValue;
  }

  navigateToCourtDeposit() {
    this.router.navigate(['court-deposit'], {
      state: {
        depositCategorySelected: this.depositCategorySelected
      }
    });
  }

  handleCourtPaymentClick(module: LandingPageModule) {
    if (module.category === PaymentCategory.COURT_PAYMENT) {
      this.navigateToCourtPayment(module.paymentTypeValue);
    }
  }

  getImageSrc(module: LandingPageModule): string {
    if (module?.documentData && module?.documentType) {
      return `data:${module.documentType};base64,${module.documentData}`;
    }
    return ''; // Return empty string if no image data
  }

  // Optional: Method to check if module has image
  hasImage(module: LandingPageModule): boolean {
    return !!module?.documentData;
  }

  constructor(private readonly router: Router, private readonly route: ActivatedRoute, private readonly announcementService: AnnouncementService,
              private readonly applicationSettingsService: ApplicationSettingsService,
              private readonly sanitizer: DomSanitizer,
              private readonly authServce: AuthService,
              private readonly errorService: ErrorService,
              private readonly landingPageModuleService: LandingPageModuleService) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const kioskCode = params['kioskCode'];
      if (kioskCode) {
        localStorage.setItem('kioskCode', kioskCode);
      }
    });
    this.errorService.errorMessage$.subscribe(message => {
      this.errorMessage = message;
    });
    const needRefresh = LocalStorageUtils.isRefreshNeeded();
    this.fetchAuthToken(() => {
      this.isTerminal = LocalStorageUtils.isOnTerminal();
      if (needRefresh){
        globalThis.location.reload()
      }
      this.updateQrGenerationAttemptNumberInLocalStorage();
      this.fetchLandingPageModules();
      if (!this.isTerminal) {
        this.fetchAnnouncements();
      }
      this.fetchAppSettings();
    });
    const authDataRaw = localStorage.getItem('authData');
    if (authDataRaw) {
      const authData = JSON.parse(authDataRaw);
      this.onScreenKeyboardEnabled = authData?.capabilities?.[0]?.onScreenKeyBoard === true;
    }
  }

  updateQrGenerationAttemptNumberInLocalStorage() {
    const key = `PAY_NOW_ATTEMPTED`;
    const updatedAttempt = 0;
    localStorage.setItem(key, String(updatedAttempt));
  }

  private fetchAnnouncements(): void {
    this.announcementService.fetchActiveAnnouncements()
      .subscribe({
        next: (response) => {
          this.announcements = response;
        },
        error: (err) => {
        }
      })
  }

  private fetchLandingPageModules(): void {
    this.landingPageModuleService.fetchLandingPageModules()
      .subscribe({
        next: (response: LandingPageModule[]) => {
          for (const landingPageModule of response) {
            if (landingPageModule.category === PaymentCategory.COURT_PAYMENT) {
              this.courtPaymentModules.push(landingPageModule);
            } else if (landingPageModule.category === PaymentCategory.COURT_DEPOSIT) {
              this.courtDepositModules.push(landingPageModule);
            }
          }
          this.chunkedCourtPaymentModules = this.chunkArray(this.courtPaymentModules, 2);
          this.chunkedCourtDepositModules = this.chunkArray(this.courtDepositModules, 2);
        },
        error: (err) => {
        }
      });
  }

  private fetchAppSettings(): void {
    this.applicationSettingsService.fetchApplicationSettingsByDataCode(AppConstants.ANNOUNCEMENT_DISPLAY_MODE)
      .subscribe({
        next: (response) => {
          const displayModeSetting = response;
          const value = displayModeSetting?.dataValue;
          const isMobileView = window.innerWidth <= 600;
          if (isMobileView) {
            this.displayMode = DisplayModes.CAROUSEL;
          } else if (value && (Object.values(DisplayModes) as string[]).includes(value)) {
            this.displayMode = value as DisplayModes;
          } else {
            this.displayMode = DisplayModes.CAROUSEL;
          }
        },
        error: (err) => {
          this.displayMode = DisplayModes.CAROUSEL;
        }
      });
  }

  getTruncatedMessage(message: string): string {
    const htmlWithAllowedTags = message.replaceAll(/<(?!\/?(a|strong|img)(\s|>))[^>]+>/g, ''); // NOSONAR
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlWithAllowedTags;
    const visibleText = tempDiv.textContent || '';
    if (visibleText.length <= 300) {
      return htmlWithAllowedTags;
    }
    return visibleText.substring(0, 300) + '...';
  }

  shouldShowReadMore(message: string): boolean {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = message;
    const visibleText = tempDiv.textContent?.trim() || '';
    if (visibleText.length <= 300) return false;
    const links = tempDiv.querySelectorAll('a');
    if (links.length) {
      const lastLink = links[links.length - 1].textContent || '';
      const linkIndex = visibleText.indexOf(lastLink);
      if (linkIndex >= visibleText.length - 50) {
        return false;
      }
    }
    return true;
  }


  openAnnouncementModal(announcement: any) {
    this.selectedAnnouncement = this.sanitizer.bypassSecurityTrustHtml(announcement.message);

    const modalElement = document.getElementById('announcementModal');
    if (modalElement) {
      const modal = new Modal(modalElement);
      modal.show();
    }
  }

  private fetchAuthToken(afterSuccess?: () => void): void {
    this.authServce.fetchAuthToken().subscribe({
      next: (response: AuthTokenResponse) => {
        this.token = response.token;
        this.authServce.setToken(response);
        localStorage.setItem('authData', JSON.stringify(response));
        if (afterSuccess) afterSuccess();
      },
      error: (err) => {
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.scanTimeout) clearTimeout(this.scanTimeout);

    if (event.key === 'Enter') {
      const data = this.scanBuffer;
      this.scanBuffer = '';
      this.onScan(data);
    } else {
      this.scanBuffer += event.key;
    }

    this.scanTimeout = setTimeout(() => this.scanBuffer = '', 200);
  }

  onScan(data: string) {
    this.scannedValue = data;
    if (this.isTerminal) {
      this.onGo(data);
    } else {
      globalThis.location.href = data;  // External navigation
    }
  }

  onGo(queryString: string) {
    let extractedQueryValues = this.extractQueryValues(queryString, ["paymentReference", "id"]);
    const paymentRef = extractedQueryValues['paymentReference'];
    const payerId = extractedQueryValues['id'];
    if (paymentRef && payerId) {
      this.router.navigate(['court-payment-details'], {
        queryParams: extractedQueryValues
      });
    }
    if (paymentRef || payerId) { //if stmt should not be the only stmt in else block
      this.errorMessage = "The QR code scanned does not have the required details. Cannot proceed further.";
    } else {
      this.errorMessage = "You have scanned an invalid QR code. Please scan Online Payment Portal QR Code.";
    }
  }

  extractQueryValues(input: string, keys: string[]): Record<string, string | null> {
    let queryString = input.split('?')[1] || '';
    queryString = queryString.replaceAll("Shift", "");
    const params = new URLSearchParams(queryString);

    const result: Record<string, string | null> = {};
    for (const key of keys) {
      result[key] = params.get(key);
    }
    return result;
  }

  handleDropdownKey(event: any) {
    return AppConstants.handleDropdownKey(event);
  }
}
